/** CoolClimate Network local government membership fee schedule (annual). */

export type MembershipType = 'affiliate' | 'regular' | 'other';

export type PopulationTier =
  | 'under_50k'
  | '50k_100k'
  | '100k_500k'
  | '500k_1m'
  | 'over_1m';

export const POPULATION_TIER_OPTIONS: { id: PopulationTier; label: string }[] = [
  { id: 'under_50k', label: 'Under 50,000' },
  { id: '50k_100k', label: '50,000 – 100,000' },
  { id: '100k_500k', label: '100,000 – 500,000' },
  { id: '500k_1m', label: '500,000 – 1,000,000' },
  { id: 'over_1m', label: 'Over 1,000,000' },
];

export const AFFILIATE_FEES: Record<PopulationTier, number> = {
  under_50k: 2500,
  '50k_100k': 5000,
  '100k_500k': 7500,
  '500k_1m': 10000,
  over_1m: 20000,
};

export const REGULAR_FEES: Record<PopulationTier, number> = {
  under_50k: 5000,
  '50k_100k': 10000,
  '100k_500k': 15000,
  '500k_1m': 20000,
  over_1m: 40000,
};

export const MEMBERSHIP_TYPE_LABELS: Record<MembershipType, string> = {
  affiliate: 'Affiliate Membership',
  regular: 'Regular Membership',
  other: 'Other / Not Sure',
};

export const ORGANIZATION_TYPES = [
  'City',
  'County',
  'Special District',
  'State Agency',
  'University / College',
  'Business',
  'Nonprofit',
  'Other',
] as const;

export type OrganizationType = (typeof ORGANIZATION_TYPES)[number];

/** Jurisdictions billed on the population-based fee schedule. */
export const POPULATION_BASED_ORG_TYPES: OrganizationType[] = ['City', 'County'];

export function usesPopulationTier(
  organizationType: OrganizationType | '',
  membershipType: MembershipType | ''
): boolean {
  return (
    membershipType !== '' &&
    membershipType !== 'other' &&
    organizationType !== '' &&
    POPULATION_BASED_ORG_TYPES.includes(organizationType as OrganizationType)
  );
}

export function isBusinessOrganizationType(organizationType: OrganizationType | ''): boolean {
  return organizationType === 'Business';
}

export const MEMBERSHIP_REQUEST_STATUSES = [
  'pending_review',
  'contacted',
  'proposal_sent',
  'agreement_sent',
  'active',
  'closed',
] as const;

export type MembershipRequestStatus = (typeof MEMBERSHIP_REQUEST_STATUSES)[number];

export const MEMBERSHIP_STATUS_LABELS: Record<MembershipRequestStatus, string> = {
  pending_review: 'Pending review',
  contacted: 'Contacted',
  proposal_sent: 'Proposal sent',
  agreement_sent: 'Agreement sent',
  active: 'Active',
  closed: 'Closed',
};

/**
 * TODO: Add CoolClimate-Membership-Agreement-2026.pdf to public/docs/ when asset is available.
 */
export const MEMBERSHIP_AGREEMENT_PDF_URL = '/docs/CoolClimate-Membership-Agreement-2026.pdf';

export function getEstimatedAnnualFee(
  membershipType: MembershipType,
  populationTier: PopulationTier | ''
): number | null {
  if (membershipType === 'other' || !populationTier) {
    return null;
  }
  if (membershipType === 'affiliate') {
    return AFFILIATE_FEES[populationTier];
  }
  return REGULAR_FEES[populationTier];
}

export function formatUsd(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount);
}

export function getPopulationTierLabel(tier: PopulationTier | ''): string {
  if (!tier) return '—';
  return POPULATION_TIER_OPTIONS.find((o) => o.id === tier)?.label ?? tier;
}
